/* ================================================================
   BRITTEN CUSTOM: Real Brides archive v2 (preview) — shared lightbox
   Location: assets/britten-archive-lightbox.js
   Called from: snippets/britten-archive.liquid, #bral — registers with
                assets/britten-archive.js via BrittenArchive.registerOverlay
                so core's URL/history rules (?p=/?w=, push vs. replace,
                the direct-arrival-closes-to-the-archive fix) drive this
                dialog from both views.

   This file owns everything INSIDE #bral: it builds the dialog's shell
   (top bar, stage, panel) at boot, so the Liquid only supplies the empty
   `#bral` element. Styles: the LIGHTBOX block of assets/britten-archive.css.

   Two modes, one dialog:
   - photo  (?p=<id>) — a single photograph. Next/Previous step through
     the current filtered set (assets/britten-archive.js's
     filteredPhotos() — search/product/shared facets, not the Photos
     view's own colour sort order, a minor simplification for this
     preview). If the open photo is not in that set (opened from her
     wedding's thumbnails, or a direct link under other filters) the whole
     photo list is the set instead, so the counter is always honest.
   - wedding (?w=<slug>) — "Quick look" from a Weddings card: her hero
     photo and the same panel, no carousel, no counter.

   Fixed stage (round 4). The arrows, the counter, Close and the panel
   never move between photos: the stage is a fixed-size box (desktop: the
   viewport minus the 420px panel; mobile: the flex area between the top
   bar and the panel) and the photo is `object-fit:contain` inside it, so
   portrait and landscape photos cannot shift anything. The previous photo
   stays on screen until the next one has decoded, then the two crossfade.
   On mobile the panel has a fixed height too (it scrolls inside if a
   wedding has several pieces), so the stage never resizes between photos.

   Panel (desktop, top to bottom): gold "№ LXXX · COUNTRY" + the name;
   "THE VEIL SHE WORE" / "SHE WORE" and a product row per VERIFIED piece
   (veil first) with a 52px SHOP button (and, for veils, "See <veil> on N
   brides"); "HER WEDDING · N PHOTOS", "See her full wedding" and four
   thumbnails of her other distinct photos; her quote; the photographer
   credit. Mobile (2b): name + number, one row per piece with an inline
   SHOP, "On N more brides" + "Her full wedding", then "Swipe for next ·
   Photography: …". Close: a bordered 44px "CLOSE ×" in the panel's top
   right on desktop, in the top bar on mobile.

   Social proof rules (the audit's "Strengthening the social proof"
   section, and the spec):
   - Only VERIFIED pieces are shown as "She wore". Verification is per
     product-link, from the data (`pr: [[productIndex, verified]]`), at
     both wedding and photo level — this file only reads the flag, never
     infers it. Associated but unverified pieces are a quiet "From her
     wedding: Name, Name" line of links (desktop), never a row or a button.
   - Her own quote, if the data carries one — italic, "— <name>". NEVER
     invented; if `q` is null/absent, nothing is shown.
   - Product image and price come from the storefront (`/products/<handle>.js`,
     through the page's market prefix so the price is in the page's currency),
     fetched lazily when a piece is first shown and cached per handle. If the
     fetch fails nothing is shown for it (a canvas block, no price) — the
     panel never waits on it.
   - Photographer credit (the data has no website today, so no link).
   - Focus trap, Esc, focus restored to whatever opened it.

   Tracking (via BrittenArchive.track -> Shopify.analytics.publish, flat
   payloads; names are listed in docs/veil-guide-custom-pixel.md):
   britten_archive_lightbox_shop_click, ..._lightbox_more_brides,
   ..._lightbox_full_wedding — each with product_handle and photo_id.

   CHANGELOG:
   - 15 Oct 2026: crops aim at the bride (fp, marketing_131); no fp = old crop. The
     four "her wedding" thumbnails set object-position from the photo's fp. The
     stage itself is object-fit:contain (whole photo) and is left alone.
   - 5 Oct 2026: Your Britten: the bar button saves the PHOTO on show ("Save photo" / "Photo saved", kind=photo,
     data-photo-id), not its wedding; a wedding is saved from its card (contract section 26). In a Quick look it
     saves the hero photo; a photo with no id in the data hides the button.
   - 2 Oct 2026: localisation — every word the dialog writes is a locale key
     (britten.js.rb.archive_*, or an existing key whose English is identical),
     each call with its English literal as the fallback, so English is
     unchanged. Numbers, the price and the country follow the page's language;
     the product type uses the old gallery's product_kind_* words.
   - 2026-09-30: Your Britten S1 — a "Save look" button in the top bar
     (button.yb-save--bar, [data-britten-save] kind=look) saves the wedding on
     show: the photo's wedding in photo mode, the wedding itself in wedding mode.
     Only when BrittenCoreConfig.flags.yourBritten is on; the core wires the
     click; syncSave() re-points the data-* and the pressed state whenever the
     photo changes, and hides the button for a photo with no wedding.
   - 2026-09-29: round 4 lightbox — bordered "CLOSE ×"; fixed-size stage
     with contained, crossfading photos so the arrows/counter/close/panel
     never move; product-first panel (product image + price from the
     storefront, Shop / See-on-N-brides buttons, "Her wedding" thumbnails,
     quote, credit); mobile top bar + fixed bottom panel per artboard 2b;
     lightbox_* tracking. Removed the generic "A wedding in <region>."
     sentence and the plain text links.
   - 2026-09-29: press marks — a wedding with `px` (featured in the press) gets
     the outlet's logo in the top-left corner of the stage photo, and an "As
     featured in [logo] Australia" line under the name, linking to the article
     (new tab) when `u` is there. Both come off again for any other wedding,
     on every render. No `px` = nothing added.
   ================================================================ */
(function () {
  'use strict';
  function log(msg) { if (window.BrittenArchiveDebug) console.log('[Britten Archive · Lightbox] ' + msg); }

  function boot() {
    var BA = window.BrittenArchive;
    if (!BA) { setTimeout(boot, 30); return; }

    var lb = document.getElementById('bral');
    if (!lb) return;

    // Every string below is translatable (window.BrittenI18n), each call with
    // its English literal as the fallback so English reads as it always has.
    // The receiver has to end in "i18n" for scripts/i18n-js-keys-check.py.
    var i18n = window.BrittenI18n;
    var safeI18n = {
      t: function (key, vars) { return i18n ? i18n.t(key, vars) : ''; },
      n: function (key, count, vars) { return i18n ? i18n.n(key, count, vars) : ''; }
    };

    // ---------------------------------------------------------------
    // Shell — built once. Everything the dialog shows lives in here.
    // ---------------------------------------------------------------
    function h(tag, cls, text) {
      var e = document.createElement(tag);
      if (cls) e.className = cls;
      if (text !== undefined && text !== null) e.textContent = text;
      return e;
    }
    function add(parent) {
      for (var i = 1; i < arguments.length; i++) if (arguments[i]) parent.appendChild(arguments[i]);
      return parent;
    }
    function button(cls, text, label) {
      var b = h('button', cls, text);
      b.type = 'button';
      if (label) b.setAttribute('aria-label', label);
      return b;
    }

    lb.textContent = '';
    var bar = h('div', 'bral__bar');
    var barCount = h('p', 'bral__count bral__count--bar');
    var closeWord = safeI18n.t('rb.lightbox_close_aria') || 'Close';
    var closeBtn = button('bral__close', null, closeWord);
    closeBtn.setAttribute('data-bral-close', '');
    add(closeBtn, h('span', 'bral__close-t', closeWord), h('span', 'bral__x', '×'));
    closeBtn.lastChild.setAttribute('aria-hidden', 'true');
    add(bar, barCount, closeBtn);

    var stage = h('div', 'bral__stage');
    var frame = h('div', 'bral__frame');
    var imgA = h('img', 'bral__img');
    var imgB = h('img', 'bral__img');
    imgA.alt = imgB.alt = '';
    imgA.decoding = imgB.decoding = 'async';
    add(frame, imgA, imgB);
    // The press badge (below) tracks the photo's own corner, so it re-places
    // itself whenever a layer finishes loading and when the window resizes.
    imgA.addEventListener('load', placeStagePress);
    imgB.addEventListener('load', placeStagePress);
    window.addEventListener('resize', placeStagePress);
    var prevBtn = button('bral__nav bral__nav--prev', '‹', safeI18n.t('rb.archive_lb_prev') || 'Previous photograph');
    var nextBtn = button('bral__nav bral__nav--next', '›', safeI18n.t('rb.archive_lb_next') || 'Next photograph');
    prevBtn.setAttribute('data-bral-prev', '');
    nextBtn.setAttribute('data-bral-next', '');
    var stageCount = h('p', 'bral__count bral__count--stage');
    add(stage, frame, prevBtn, nextBtn, stageCount);

    var panel = h('div', 'bral__panel');
    panel.setAttribute('data-bral-panel', '');
    add(lb, bar, stage, panel);

    var currentMode = null; // 'photo' | 'wedding' | null (closed)
    var currentId = null;
    var lastFocus = null;
    var pendingPhoto = null;
    var pendingWedding = null;

    // ---------------------------------------------------------------
    // The photo: two stacked layers in the fixed frame. The layer on
    // show stays until the next photo has loaded AND decoded, then the
    // next one fades in over it (no blank frame, no layout change).
    // ---------------------------------------------------------------
    // (`front` is the layer on show; the first photo after opening lands in imgA.)
    var front = imgB, back = imgA, paintTok = 0, fadeTimer = 0;

    // ---------------------------------------------------------------
    // Press badge on the stage: the outlet's logo in the top-left corner of the
    // PHOTO itself (object-fit:contain letterboxes it inside the frame, so the
    // corner is worked out from the photo's own size), for a wedding that has
    // a `px`. Decorative (the words are in the panel line). It is set for every
    // render, comes off with the dialog, waits until its photo is on show, and
    // is kept as it is when the next photo has the same outlet, so it neither
    // sticks to another bride's photo nor blinks between one bride's.
    // ---------------------------------------------------------------
    var stagePress = null, stagePressKey = '';
    function syncStagePress(w) {
      var info = BA.press(w);
      var key = info ? info.id : '';
      if (key === stagePressKey && (stagePress || !key)) return;
      if (stagePress && stagePress.parentNode) stagePress.parentNode.removeChild(stagePress);
      stagePress = key ? BA.pressBadge(w, 'bral__press') : null;
      stagePressKey = stagePress ? key : '';
      if (stagePress) { stagePress.hidden = true; frame.appendChild(stagePress); }
    }
    function placeStagePress() {
      if (!stagePress || stagePress.hidden) return;
      var iw = front.naturalWidth, ih = front.naturalHeight;
      var fw = frame.clientWidth, fh = frame.clientHeight;
      var x = 0, y = 0;
      if (iw && ih && fw && fh) {
        var sc = Math.min(fw / iw, fh / ih);
        x = (fw - iw * sc) / 2;
        y = (fh - ih * sc) / 2;
      }
      stagePress.style.left = x + 'px';
      stagePress.style.top = y + 'px';
    }
    function showStagePress() {
      if (!stagePress) return;
      stagePress.hidden = false;
      placeStagePress();
    }
    // No photo on show (none to load, or it would not load): no badge over the gap.
    function hideStagePress() { if (stagePress) stagePress.hidden = true; }

    function isShopifyCdn(u) { return /^https?:\/\/cdn\.shopify\.com\//.test(u || ''); }
    function cdnUrl(u, w) { return u + (u.indexOf('?') > -1 ? '&' : '?') + 'width=' + w; }
    function heroSrc(u, cdn) { return u ? ((cdn || isShopifyCdn(u)) ? cdnUrl(u, 1800) : u) : ''; }

    // ---------------------------------------------------------------
    // Your Britten: "Save photo" in the top bar. The core (britten-core.js)
    // wires the click, the toast, the events and aria-pressed for every
    // [data-britten-save]; this builds the button once, on first use (the core
    // config is written after this script can first run), and re-points it at
    // the photo on show each time the photo changes. A photo is keyed by its
    // id (the media asset's uuid); its wedding's slug and number ride along.
    // (`i18n`, the receiver the keys check looks for, is declared at the top.)
    // ---------------------------------------------------------------
    var saveBtn = null, saveArgs = null;
    function yourBrittenOn() {
      var c = window.BrittenCoreConfig;
      return !!(c && c.flags && c.flags.yourBritten);
    }
    function ensureSave() {
      if (saveBtn) return saveBtn;
      if (!yourBrittenOn()) return null;
      saveBtn = button('britten-core-save yb-save yb-save--bar', null);
      saveBtn.setAttribute('data-britten-save', '');
      saveBtn.setAttribute('data-kind', 'photo');
      saveBtn.setAttribute('data-surface', 'lightbox');
      saveBtn.setAttribute('aria-pressed', 'false');
      saveBtn.hidden = true;
      saveBtn.innerHTML = '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M7 4h10a1 1 0 0 1 1 1v15l-6-4.2L6 20V5a1 1 0 0 1 1-1z"/></svg>';
      add(saveBtn,
        h('span', 'yb-save__text yb-save__text--off', (i18n && i18n.t('js.yb.save_photo')) || 'Save photo'),
        h('span', 'yb-save__text yb-save__text--on', (i18n && i18n.t('js.yb.saved_photo')) || 'Photo saved'));
      bar.insertBefore(saveBtn, closeBtn);
      return saveBtn;
    }
    function syncSave(w, photoId, url) {
      var b = ensureSave();
      saveArgs = [w, photoId, url];
      if (!b) return;
      if (!w || !(w.s || w.an) || !photoId) { b.hidden = true; return; }
      b.setAttribute('data-look-slug', w.s || '');
      b.setAttribute('data-look-archive-no', w.an || '');
      b.setAttribute('data-title', w.b || '');
      b.setAttribute('data-image', url ? (isShopifyCdn(url) ? cdnUrl(url, 640) : url) : '');
      b.setAttribute('data-photo-id', photoId);
      var core = window.BrittenCore;
      var hit = core && core.items && core.items.has({ kind: 'photo', photo_id: photoId });
      b.setAttribute('aria-pressed', hit ? 'true' : 'false');
      b.hidden = false;
    }
    // A save, an undo or another tab changes the items: the button follows (the core's own render does it too).
    document.addEventListener('britten:items:change', function () { if (currentMode && saveArgs) syncSave(saveArgs[0], saveArgs[1], saveArgs[2]); });

    function layerOff(el) { el.style.transition = 'none'; el.style.opacity = '0'; }
    function layerOn(el) { el.style.transition = 'none'; el.style.opacity = '1'; }
    // Finish any fade in progress: the newest layer to full, the other away.
    function settleFade() {
      clearTimeout(fadeTimer);
      if (front.getAttribute('src')) layerOn(front); else layerOff(front);
      layerOff(back);
    }
    function clearStage() {
      paintTok++;
      clearTimeout(fadeTimer);
      [imgA, imgB].forEach(function (el) { layerOff(el); el.removeAttribute('src'); el.alt = ''; });
      front = imgB;
      back = imgA;
      syncStagePress(null);
    }
    function swapIn(src, alt) {
      settleFade();
      var next = back;
      next.alt = alt || '';
      next.src = src;
      layerOff(next);
      next.style.zIndex = '2';
      front.style.zIndex = '1';
      void next.offsetWidth; // commit opacity:0 so the fade runs
      next.style.transition = '';
      next.style.opacity = '1';
      var old = front;
      front = next;
      back = old;
      fadeTimer = setTimeout(function () { layerOff(old); }, 320);
      showStagePress();
    }
    function paintImage(u, cdn, alt) {
      var src = heroSrc(u, cdn);
      var tok = ++paintTok;
      if (!src) { settleFade(); layerOff(front); hideStagePress(); return; }
      if (front.getAttribute('src') === src && front.style.opacity === '1') { showStagePress(); return; }
      var loader = new Image();
      function go() { if (tok === paintTok && currentMode) swapIn(src, alt); }
      loader.onload = function () {
        var ready = loader.decode ? loader.decode() : Promise.resolve();
        ready.then(go, go);
      };
      // A photo that will not load: leave the stage empty rather than keep a
      // different wedding's photo beside this wedding's panel.
      loader.onerror = function () { if (tok === paintTok) { settleFade(); layerOff(front); hideStagePress(); } };
      loader.src = src;
    }

    // Is a click on the stage inside the photo itself (object-fit:contain
    // letterboxes it inside the frame)? Only clicks OUTSIDE it are "backdrop".
    function insideImage(e) {
      var r = front.getBoundingClientRect();
      var nw = front.naturalWidth, nh = front.naturalHeight;
      if (!nw || !nh) return false;
      var s = Math.min(r.width / nw, r.height / nh), w = nw * s, ht = nh * s;
      var x0 = r.left + (r.width - w) / 2, y0 = r.top + (r.height - ht) / 2;
      return e.clientX >= x0 && e.clientX <= x0 + w && e.clientY >= y0 && e.clientY <= y0 + ht;
    }

    // ---------------------------------------------------------------
    // Data helpers
    // ---------------------------------------------------------------
    // The product table's own `ty` (storefront product type) can come back
    // null for every product in a given pipeline run (seen live: a
    // rate-limited liveness probe left all 128 types unset) — fall back to
    // the same handle/title sniff britten-real-brides-page.liquid's
    // productKind() uses, so "The veil she wore" and "See this veil on
    // more brides" still work from the data this build actually has.
    // Lower-case always: the data's `ty` is a display label ("Veil",
    // "Hair vine"); comparisons below use lower-case ids.
    function productType(p) {
      if (p.ty) return String(p.ty).toLowerCase();
      return /veil/i.test((p.h || '') + ' ' + (p.t || '')) ? 'veil' : '';
    }
    function isVeil(item) { return productType(item.p) === 'veil'; }

    function productsFor(pairs) {
      return (pairs || []).map(function (pair) {
        var p = BA.productList()[pair[0]];
        return p ? { p: p, verified: !!pair[1] } : null;
      }).filter(Boolean);
    }

    var ROMAN = [[1000, 'M'], [900, 'CM'], [500, 'D'], [400, 'CD'], [100, 'C'], [90, 'XC'], [50, 'L'], [40, 'XL'], [10, 'X'], [9, 'IX'], [5, 'V'], [4, 'IV'], [1, 'I']];
    function toRoman(n) {
      var out = '';
      n = Math.floor(n);
      if (!(n > 0)) return '';
      ROMAN.forEach(function (pair) { while (n >= pair[0]) { out += pair[1]; n -= pair[0]; } });
      return out;
    }
    function fmtInt(n) { return BA.fmt(Number(n)); }

    // "№ LXXX · SCOTLAND" (upper-cased by CSS). Either half may be absent.
    // The place is her COUNTRY: the data's `ct` ("Italy", "United States"),
    // except that a wedding in one of the UK's nations reads by that nation
    // ("Scotland", as in the design, not "United Kingdom") — taken from the
    // end of `rg` ("Edinburgh, Scotland"). No country on record: `rg` as is.
    var UK_NATIONS = /^(england|scotland|wales|northern ireland)$/i;
    function placeLabel(w) {
      var rg = String(w.rg || '').trim();
      var last = rg.split(',').pop().trim();
      if (UK_NATIONS.test(last)) return last;
      // The country in the page's language, by its ISO code ('' in English).
      return (w.cc && BA.regionName(w.cc)) || w.ct || rg;
    }
    function numberLine(w) {
      if (!w) return '';
      var no = w.an ? '\u2116 ' + toRoman(w.an) : '';
      return [no, placeLabel(w)].filter(Boolean).join(' \u00b7 ');
    }

    function findPhoto(id) {
      var list = (BA.data.photos && BA.data.photos.photos) || [];
      for (var i = 0; i < list.length; i++) if (list[i].id === id) return list[i];
      return null;
    }
    function findWedding(slug) {
      var list = (BA.data.archive && BA.data.archive.weddings) || [];
      for (var i = 0; i < list.length; i++) if (list[i].s === slug) return list[i];
      return null;
    }

    // ---- her other photos: the SAME dedupe as the wedding-card strip in
    // britten-archive-weddings.js (extraPhotos): never the photo on show,
    // never the same id / URL / base filename twice (a re-upload's `_<uuid>`
    // suffix is stripped, that is a near-duplicate). Photos JSON order.
    function urlKey(u) { return String(u || '').split('?')[0].toLowerCase(); }
    function fileKey(u) {
      var name = urlKey(u).split('/').pop();
      return name.replace(/_[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}(?=\.[a-z0-9]+$)/, '');
    }
    var byWedding = null, weddingIdx = null;
    function buildPhotoIndex() {
      if (byWedding) return true;
      var photos = BA.data.photos && BA.data.photos.photos;
      var weddings = BA.data.archive && BA.data.archive.weddings;
      if (!photos || !weddings) return false;
      weddingIdx = {};
      weddings.forEach(function (w, i) { weddingIdx[w.s] = i; });
      byWedding = {};
      photos.forEach(function (p) {
        if (p.w === null || p.w === undefined) return;
        (byWedding[p.w] || (byWedding[p.w] = [])).push(p);
      });
      return true;
    }
    // null = the photos JSON is not here yet.
    function otherPhotos(w, seedUrl, seedId) {
      if (!w || !buildPhotoIndex()) return null;
      var mine = byWedding[weddingIdx[w.s]] || [];
      var seenId = {}, seenUrl = {}, seenFile = {};
      if (seedId) seenId[seedId] = 1;
      if (seedUrl) { seenUrl[urlKey(seedUrl)] = 1; seenFile[fileKey(seedUrl)] = 1; }
      var out = [];
      mine.forEach(function (p) {
        if (!p || !p.u || !p.id) return;
        var uk = urlKey(p.u), fk = fileKey(p.u);
        if (seenId[p.id] || seenUrl[uk] || seenFile[fk]) return;
        seenId[p.id] = 1; seenUrl[uk] = 1; seenFile[fk] = 1;
        out.push(p);
      });
      return out;
    }
    function photoIdForUrl(w, u) {
      if (!w || !u || !buildPhotoIndex()) return '';
      var mine = byWedding[weddingIdx[w.s]] || [];
      for (var i = 0; i < mine.length; i++) if (urlKey(mine[i].u) === urlKey(u)) return mine[i].id;
      return '';
    }

    // ---------------------------------------------------------------
    // Storefront product data (image + price). Lazy, cached per handle,
    // silent on failure.
    // ---------------------------------------------------------------
    var productCache = {}; // handle -> { state:'pending'|'ok'|'fail', cbs:[], img, price, type }

    // On a market page (/en-us/…) the storefront answers /products/<h>.js in
    // THAT market's currency, so fetch through the page's own route prefix
    // (the theme's Shopify.routes.root_url, "" on the main market) and label the
    // price with the currency the page is in (Shopify.currency.active, else GBP).
    function routePrefix() {
      var r = window.Shopify && window.Shopify.routes && (window.Shopify.routes.root_url || window.Shopify.routes.root);
      return typeof r === 'string' ? r.replace(/\/+$/, '') : '';
    }
    function currencyCode() {
      var c = window.Shopify && window.Shopify.currency && window.Shopify.currency.active;
      return /^[A-Z]{3}$/.test(c || '') ? c : 'GBP';
    }
    // pence -> "£95" / "£95.50"
    function formatPrice(pence) {
      var whole = Math.round(pence) % 100 === 0;
      var v = pence / 100;
      try {
        return new Intl.NumberFormat(BA.isEnglish ? 'en-GB' : BA.lang, { style: 'currency', currency: currencyCode(),
          minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: whole ? 0 : 2 }).format(v);
      } catch (e) {
        return '£' + (whole ? String(v) : v.toFixed(2));
      }
    }
    function productImageUrl(u) {
      if (!u) return '';
      u = String(u);
      if (u.indexOf('//') === 0) u = 'https:' + u;
      return (isShopifyCdn(u) || u.indexOf('/cdn/shop/') > -1) ? cdnUrl(u, 300) : u;
    }
    function parseProduct(j) {
      var prices = ((j && j.variants) || []).map(function (v) { return Number(v.price); }).filter(function (n) { return isFinite(n) && n > 0; });
      var price = '';
      if (prices.length) {
        var lo = Math.min.apply(null, prices), hi = Math.max.apply(null, prices);
        price = lo !== hi ? (safeI18n.t('js.rb.df_price_from', { price: formatPrice(lo) }) || 'from ' + formatPrice(lo)) : formatPrice(lo);
      }
      var img = (j && (j.featured_image || (j.images && j.images[0]))) || '';
      return { img: productImageUrl(img), price: price, type: (j && j.type) || '' };
    }
    function loadProduct(handle, cb) {
      var c = productCache[handle];
      if (c) { if (c.state === 'pending') c.cbs.push(cb); else cb(c); return; }
      c = productCache[handle] = { state: 'pending', cbs: [cb], img: '', price: '', type: '' };
      function finish(ok, d) {
        if (ok) { c.img = d.img; c.price = d.price; c.type = d.type; }
        c.state = ok ? 'ok' : 'fail';
        var cbs = c.cbs; c.cbs = [];
        cbs.forEach(function (fn) { try { fn(c); } catch (e) { log('product callback failed: ' + e); } });
      }
      if (!window.fetch) { finish(false); return; }
      try {
        window.fetch(routePrefix() + '/products/' + encodeURIComponent(handle) + '.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
          .then(function (r) { if (!r.ok) throw new Error('status ' + r.status); return r.json(); })
          .then(function (j) { finish(true, parseProduct(j)); })
          .then(null, function () { finish(false); });
      } catch (e) { finish(false); }
    }

    // ---------------------------------------------------------------
    // Panel
    // ---------------------------------------------------------------
    // The product type in the page's language, where the storefront has a word
    // for it (the same keys the old gallery uses). `t` is the lower-case English
    // type; English, and a type with no key, come back as they were.
    // i18n-keys-prefix: js.rb.product_kind_
    var KIND_KEYS = {
      veil: 'js.rb.product_kind_veil', 'hair vine': 'js.rb.product_kind_hair_vine',
      earrings: 'js.rb.product_kind_earrings', necklace: 'js.rb.product_kind_necklace',
      bracelet: 'js.rb.product_kind_bracelet', garter: 'js.rb.product_kind_garter',
      'hair pin': 'js.rb.product_kind_hair_pin', comb: 'js.rb.product_kind_comb',
      headpiece: 'js.rb.product_kind_headpiece', belt: 'js.rb.product_kind_belt'
    };
    function kindWord(t) {
      var key = BA.isEnglish ? '' : KIND_KEYS[t];
      return (key && safeI18n.t(key)) || t;
    }
    function typeLabel(p) {
      var t = productType(p);
      if (t && !BA.isEnglish && kindWord(t) !== t) { var w = kindWord(t); return w.charAt(0).toUpperCase() + w.slice(1); }
      if (p.ty) return String(p.ty);
      return t ? t.charAt(0).toUpperCase() + t.slice(1) : '';
    }
    // Mobile shows "Niacae veil": the name plus the type, unless the name
    // already says it.
    function kindSuffix(p, name) {
      var t = productType(p);
      if (!t || name.toLowerCase().indexOf(t) > -1) return '';
      return ' ' + kindWord(t);
    }

    // ctx: { photoId, handle (the primary verified piece), name, wedding }
    function pieceEl(item, idx, ctx) {
      var p = item.p;
      var name = p.s || p.t || safeI18n.t('js.rb.archive_this_piece') || 'this piece';
      var piece = h('div', 'bral__piece');
      var row = h('div', 'bral__row');

      var media = h('div', 'bral__pimg');
      var info = h('div', 'bral__pinfo');
      if (idx === 0) info.appendChild(h('p', 'bral__eyebrow bral__eyebrow--m', safeI18n.t('js.rb.archive_she_wore') || 'She wore'));
      var nameEl = h('p', 'bral__pname');
      add(nameEl, h('span', null, name), h('span', 'bral__pkind', kindSuffix(p, name)));
      var typeEl = h('p', 'bral__ptype');
      var prov = p.nw
        ? (safeI18n.n('js.rb.archive_prov_worn', p.nw, { n: fmtInt(p.nw) }) || 'Handmade in Bath · worn by ' + fmtInt(p.nw) + ' Britten ' + (p.nw === 1 ? 'bride' : 'brides'))
        : (safeI18n.t('js.rb.archive_prov') || 'Handmade in Bath');
      add(info, nameEl, typeEl, h('p', 'bral__pprov', prov));
      add(row, media, info);
      piece.appendChild(row);

      function setType(extra) {
        var txt = [typeLabel(p) || (extra && extra.type) || '', extra && extra.price].filter(Boolean).join(' · ');
        typeEl.textContent = txt;
        typeEl.hidden = !txt;
      }
      setType(null);
      loadProduct(p.h, function (info2) {
        if (info2.state !== 'ok') return;
        setType(info2);
        if (info2.img) {
          var im = h('img');
          im.alt = '';
          im.decoding = 'async';
          im.onerror = function () { if (im.parentNode) im.parentNode.removeChild(im); };
          im.src = info2.img;
          media.appendChild(im);
        }
      });

      var shop = h('a', 'bral__shop');
      shop.href = routePrefix() + '/products/' + p.h;
      var shopName = safeI18n.t('js.rb.archive_shop_name', { name: name }) || 'Shop ' + name;
      shop.setAttribute('aria-label', shopName);
      add(shop, h('span', 'bral__shop-full', shopName), h('span', 'bral__shop-short', safeI18n.t('js.rb.archive_shop_short') || 'Shop'));
      shop.addEventListener('click', function () {
        BA.track('lightbox_shop_click', { product_handle: p.h, photo_id: ctx.photoId });
      });
      piece.appendChild(shop);

      if (isVeil(item)) {
        var see = button('bral__see', p.nw
          ? (safeI18n.n('js.rb.archive_see_on_n', p.nw, { name: name, n: fmtInt(p.nw) }) || 'See ' + name + ' on ' + fmtInt(p.nw) + ' ' + (p.nw === 1 ? 'bride' : 'brides'))
          : (safeI18n.t('js.rb.archive_see_on_more', { name: name }) || 'See ' + name + ' on more brides'));
        see.addEventListener('click', function () { moreBrides(item, ctx); });
        piece.appendChild(see);
      }
      return piece;
    }

    // Leave the dialog for the archive: clear the open-photo state first
    // (core keeps ?p=/?w= in the URL while state.p/w are set, and a reload
    // would reopen the lightbox), then run the archive change.
    function leave(fn) {
      var st = BA.getState();
      st.p = '';
      st.w = '';
      hideDialog();
      fn();
    }
    function moreBrides(item, ctx) {
      BA.track('lightbox_more_brides', { product_handle: item.p.h, photo_id: ctx.photoId });
      leave(function () {
        BA.switchView('photos');
        BA.setProduct(item.p.h);
      });
    }
    // "See her full wedding": her own page if published, else her name in
    // the Weddings view. cls/label differ between desktop and mobile.
    function fullWeddingEl(ctx, cls, label) {
      var w = ctx.wedding;
      if (!w) return null;
      function tracked() { BA.track('lightbox_full_wedding', { product_handle: ctx.handle, photo_id: ctx.photoId }); }
      if (w.hr) {
        var a = h('a', cls, label);
        a.href = w.hr;
        a.addEventListener('click', tracked);
        return a;
      }
      if (!w.s) return null;
      var b = button(cls, label);
      b.addEventListener('click', function () {
        tracked();
        leave(function () {
          BA.switchView('weddings');
          if (ctx.name) BA.runSearch(ctx.name);
        });
      });
      return b;
    }

    // The four thumbnails. They need the photos JSON — in photo mode it is
    // here already; in wedding mode fill them in when it lands.
    var THUMBS = 4, THUMB_WIDTH = 240;
    // Crop focus point (fp, marketing_131): [x, y] per cent across / down.
    // Returns an object-position value, or '' when fp is missing or malformed.
    function fpPos(p) {
      var f = p && p.fp;
      if (!f || !Array.isArray(f) || f.length !== 2) return '';
      var x = f[0], y = f[1];
      if (typeof x !== 'number' || typeof y !== 'number' || !isFinite(x) || !isFinite(y)) return '';
      if (x < 0 || x > 100 || y < 0 || y > 100) return '';
      return Math.round(x) + '% ' + Math.round(y) + '%';
    }
    function fillThumbs(wrap, ctx) {
      var others = otherPhotos(ctx.wedding, ctx.seedUrl, ctx.photoIdSeed);
      if (!others) return false;
      var owner = ctx.name ? (safeI18n.t('js.rb.archive_wedding_of', { name: ctx.name }) || ctx.name + '’s wedding') : (safeI18n.t('js.rb.archive_this_wedding') || 'this wedding');
      others.slice(0, THUMBS).forEach(function (p, i) {
        var t = button('bral__thumb', null, safeI18n.t('js.rb.archive_show_photo_aria', { n: i + 1, owner: owner }) || 'Show photograph ' + (i + 1) + ' of ' + owner);
        var im = h('img');
        im.alt = '';
        im.loading = 'lazy';
        im.decoding = 'async';
        im.src = (p.cdn || isShopifyCdn(p.u)) ? cdnUrl(p.u, THUMB_WIDTH) : p.u;
        var thumbPos = fpPos(p);
        if (thumbPos) im.style.objectPosition = thumbPos;
        t.appendChild(im);
        t.addEventListener('click', function () { BA.openPhoto(p.id, { push: false }); });
        wrap.appendChild(t);
      });
      wrap.hidden = !others.length;
      if (ctx.countEl && !ctx.total) ctx.countEl.textContent = safeI18n.n('js.rb.archive_her_wedding_n', others.length + 1, { n: others.length + 1 }) ||
        'Her wedding · ' + (others.length + 1) + ' photo' + (others.length ? 's' : '');
      return true;
    }

    // ctx = { wedding, name, photoId, seedUrl, photoIdSeed, products, quote, photographer, handle, total }
    function buildPanel(ctx) {
      var w = ctx.wedding;
      var hadThumbFocus = !!(document.activeElement && panel.contains(document.activeElement) &&
        document.activeElement.classList.contains('bral__thumb'));
      panel.textContent = '';
      panel.scrollTop = 0;

      var verified = ctx.products.filter(function (x) { return x.verified; })
        .map(function (x, i) { return { x: x, i: i }; })
        .sort(function (a, b) { return (isVeil(b.x) ? 1 : 0) - (isVeil(a.x) ? 1 : 0) || a.i - b.i; })
        .map(function (o) { return o.x; });
      var unverified = ctx.products.filter(function (x) { return !x.verified; });
      ctx.handle = verified.length ? verified[0].p.h : '';

      // 1. gold number + country, then the name
      var head = h('div', 'bral__head');
      head.appendChild(h('h2', 'bral__name', ctx.name || safeI18n.t('rb.bride_fallback_generic_name') || 'A Britten bride'));
      var num = numberLine(w);
      if (num) head.appendChild(h('p', 'bral__num', num));
      panel.appendChild(head);

      // 1b. "As featured in [logo] Australia" — only for a wedding with `px`.
      var pressInfo = BA.press(w);
      var pressMark = pressInfo && BA.pressMark(pressInfo.id);
      if (pressMark) {
        var pressLine = h('p', 'bral__press-line');
        pressLine.appendChild(h('span', 'bral__press-lead', safeI18n.t('rb.v2_press_featured_in') || 'As featured in'));
        pressLine.appendChild(document.createTextNode(' '));
        var pressWrap = pressInfo.url ? h('a', 'bral__press-mark') : h('span', 'bral__press-mark');
        if (pressInfo.url) {
          pressWrap.href = pressInfo.url;
          pressWrap.target = '_blank';
          pressWrap.rel = 'noopener';
        }
        pressWrap.appendChild(pressMark);
        if (pressInfo.suffix) {
          pressWrap.appendChild(document.createTextNode(' '));
          pressWrap.appendChild(h('span', 'bral__press-sfx', pressInfo.suffix));
        }
        pressLine.appendChild(pressWrap);
        panel.appendChild(pressLine);
      }

      // 2-4. verified pieces only, veil first
      if (verified.length) {
        var oneType = verified.length === 1 ? productType(verified[0].p) : '';
        var label = oneType === 'veil' ? (safeI18n.t('js.rb.archive_the_veil_she_wore') || 'The veil she wore')
          : (oneType ? (safeI18n.t('js.rb.archive_the_type_she_wore', { type: kindWord(oneType) }) || 'The ' + oneType + ' she wore')
            : (safeI18n.t('js.rb.archive_she_wore') || 'She wore'));
        var block = h('div', 'bral__block');
        block.appendChild(h('p', 'bral__eyebrow bral__d', label));
        var list = h('div', 'bral__pieces');
        verified.forEach(function (item, i) { list.appendChild(pieceEl(item, i, ctx)); });
        block.appendChild(list);
        panel.appendChild(block);
      }
      // Associated but unverified: quiet, desktop only, never a row or button.
      if (unverified.length) {
        var more = h('p', 'bral__more bral__d', (safeI18n.t('js.rb.archive_from_her_wedding') || 'From her wedding:') + ' ');
        unverified.forEach(function (item, i) {
          if (i) more.appendChild(document.createTextNode(', '));
          var a = h('a', null, item.p.s || item.p.t);
          a.href = routePrefix() + '/products/' + item.p.h;
          a.addEventListener('click', function () {
            BA.track('lightbox_shop_click', { product_handle: item.p.h, photo_id: ctx.photoId, kind: 'from-her-wedding' });
          });
          more.appendChild(a);
        });
        panel.appendChild(more);
      }

      // 5. her wedding + thumbnails (desktop)
      if (w) {
        var wed = h('div', 'bral__wed bral__d');
        var wedHead = h('div', 'bral__wed-head');
        var countEl = h('span', 'bral__eyebrow', w.n
          ? (safeI18n.n('js.rb.archive_her_wedding_n', w.n, { n: w.n }) || 'Her wedding · ' + w.n + ' photo' + (w.n === 1 ? '' : 's'))
          : (safeI18n.t('js.rb.archive_her_wedding') || 'Her wedding'));
        ctx.countEl = countEl;
        ctx.total = w.n || 0;
        add(wedHead, countEl, fullWeddingEl(ctx, 'bral__wed-link', safeI18n.t('js.rb.archive_see_full_wedding') || 'See her full wedding'));
        var thumbs = h('div', 'bral__thumbs');
        thumbs.hidden = true;
        add(wed, wedHead, thumbs);
        panel.appendChild(wed);
        if (!fillThumbs(thumbs, ctx)) {
          BA.onPhotosReady(function () { if (document.body.contains(thumbs)) fillThumbs(thumbs, ctx); });
        }
        if (hadThumbFocus && thumbs.firstChild) thumbs.firstChild.focus();
      }

      // 6. her quote — never invented; only rendered when the data has one.
      if (ctx.quote && ctx.quote.t) {
        var qw = h('div', 'bral__qwrap bral__d');
        qw.appendChild(h('p', 'bral__quote', '“' + ctx.quote.t + '”'));
        if (ctx.quote.by) qw.appendChild(h('p', 'bral__quote-by', '— ' + ctx.quote.by));
        panel.appendChild(qw);
      }

      // 7. photographer credit (desktop)
      function creditEl(cls, lead) {
        if (!ctx.photographer && !lead) return null;
        var credit = h('p', cls);
        if (lead) credit.appendChild(document.createTextNode(lead + (ctx.photographer ? ' · ' : '')));
        if (ctx.photographer) {
          credit.appendChild(document.createTextNode((safeI18n.t('js.rb.archive_photography') || 'Photography:') + ' '));
          if (ctx.photographerUrl && /^https?:\/\//i.test(ctx.photographerUrl)) {
            var cl = h('a', null, ctx.photographer);
            cl.href = ctx.photographerUrl;
            cl.target = '_blank';
            cl.rel = 'noopener noreferrer nofollow';
            credit.appendChild(cl);
          } else {
            credit.appendChild(document.createTextNode(ctx.photographer));
          }
        }
        return credit;
      }
      var credit = creditEl('bral__credit bral__d');
      if (credit) panel.appendChild(credit);

      // Mobile: the two outlined buttons, then the swipe/credit line.
      var acts = h('div', 'bral__macts bral__m');
      var veil = null;
      verified.forEach(function (item) { if (!veil && isVeil(item)) veil = item; });
      if (veil) {
        var mb = button('bral__mbtn', veil.p.nw
          ? (safeI18n.t('js.rb.archive_on_n_more', { n: fmtInt(veil.p.nw) }) || 'On ' + fmtInt(veil.p.nw) + ' more brides')
          : (safeI18n.t('js.rb.archive_on_more') || 'On more brides'));
        mb.addEventListener('click', function () { moreBrides(veil, ctx); });
        acts.appendChild(mb);
      }
      var fw = fullWeddingEl(ctx, 'bral__mbtn', safeI18n.t('js.rb.archive_her_full_wedding') || 'Her full wedding');
      if (fw) acts.appendChild(fw);
      if (acts.firstChild) panel.appendChild(acts);
      var foot = creditEl('bral__mfoot bral__m', currentMode === 'photo' ? (safeI18n.t('js.rb.archive_swipe_next') || 'Swipe for next') : '');
      if (foot) panel.appendChild(foot);
    }

    // ---------------------------------------------------------------
    // Counter + arrows
    // ---------------------------------------------------------------
    function navList() {
      var f = BA.filteredPhotos();
      for (var i = 0; i < f.length; i++) if (f[i].id === currentId) return f;
      return (BA.data.photos && BA.data.photos.photos) || f;
    }
    function updateNav() {
      var showNav = currentMode === 'photo';
      prevBtn.hidden = !showNav;
      nextBtn.hidden = !showNav;
      stageCount.hidden = !showNav;
      barCount.hidden = !showNav;
      if (!showNav) return;
      var list = navList(), idx = -1;
      for (var i = 0; i < list.length; i++) if (list[i].id === currentId) { idx = i; break; }
      var txt = (idx < 0 ? '–' : fmtInt(idx + 1)) + ' / ' + fmtInt(list.length);
      barCount.textContent = txt;
      stageCount.textContent = txt + ' · ' + (safeI18n.t('js.rb.archive_browse_hint') || '← → to browse');
    }

    // ---------------------------------------------------------------
    // Render
    // ---------------------------------------------------------------
    function renderPhoto(p) {
      currentMode = 'photo';
      currentId = p.id;
      var w = BA.photoWedding(p) || null;
      var name = w && w.b;
      syncStagePress(w);
      paintImage(p.u, p.cdn, BA.photoAlt(p) || (name ? (safeI18n.t('js.rb.archive_lb_alt_named', { name: name }) || name + ' at her wedding')
        : (safeI18n.t('js.rb.archive_wedding_alt_generic') || 'A real Britten wedding')));
      lb.setAttribute('aria-label', name ? (safeI18n.t('js.rb.archive_lb_aria_photo_named', { name: name }) || name + ', photograph')
        : (safeI18n.t('rb.archive_lb_photograph') || 'Photograph'));
      buildPanel({
        wedding: w, name: name, photoId: p.id, seedUrl: p.u, photoIdSeed: p.id,
        products: productsFor(p.pr), quote: (w && w.q) || null,
        photographer: (w && w.ph) || null, photographerUrl: null
      });
      syncSave(w, p.id, p.u);
      updateNav();
    }
    function renderWedding(w) {
      currentMode = 'wedding';
      currentId = w.s;
      var heroUrl = w.hero && w.hero.u;
      syncStagePress(w);
      paintImage(heroUrl, true, w.b ? (safeI18n.t('js.rb.archive_wedding_of', { name: w.b }) || w.b + '’s wedding')
        : (safeI18n.t('js.rb.archive_wedding_alt_generic') || 'A real Britten wedding'));
      lb.setAttribute('aria-label', w.b ? (safeI18n.t('js.rb.archive_lb_aria_wedding_named', { name: w.b }) || w.b + ', wedding')
        : (safeI18n.t('js.rb.archive_lb_wedding') || 'Wedding'));
      buildPanel({
        wedding: w, name: w.b, photoId: photoIdForUrl(w, heroUrl), seedUrl: heroUrl, photoIdSeed: '',
        products: productsFor(w.pr), quote: w.q || null,
        photographer: w.ph || null, photographerUrl: null
      });
      syncSave(w, photoIdForUrl(w, heroUrl), heroUrl);
      updateNav();
    }

    function showDialog(opener) {
      if (currentMode) return; // already showing
      lastFocus = opener || document.activeElement || null;
      clearStage();
      lb.hidden = false;
      lb.classList.add('bral--open');
      requestAnimationFrame(function () { lb.classList.add('bral--in'); });
      document.documentElement.style.overflow = 'hidden';
      closeBtn.focus();
    }
    function hideDialog() {
      if (!lb.classList.contains('bral--open')) return;
      paintTok++;
      lb.classList.remove('bral--in');
      document.documentElement.style.overflow = '';
      setTimeout(function () { if (currentMode) return; lb.classList.remove('bral--open'); lb.hidden = true; }, 200);
      currentMode = null;
      currentId = null;
      if (lastFocus && document.contains(lastFocus) && lastFocus.focus) lastFocus.focus();
      lastFocus = null;
    }

    function openPhoto(id, opener) {
      var p = findPhoto(id);
      if (!p) { pendingPhoto = id; return; }
      pendingPhoto = null;
      showDialog(opener);
      renderPhoto(p);
    }
    function openWedding(slug, opener) {
      var w = findWedding(slug);
      if (!w) { pendingWedding = slug; return; }
      pendingWedding = null;
      showDialog(opener);
      renderWedding(w);
    }
    function close() {
      hideDialog();
    }

    function step(dir) {
      if (currentMode !== 'photo') return;
      var list = navList();
      if (!list.length) return;
      var idx = -1;
      for (var i = 0; i < list.length; i++) if (list[i].id === currentId) { idx = i; break; }
      var next = list[((idx < 0 ? 0 : idx) + dir + list.length) % list.length];
      BA.openPhoto(next.id, { push: false });
    }

    closeBtn.addEventListener('click', function () { BA.closeOverlay(); });
    prevBtn.addEventListener('click', function () { step(-1); });
    nextBtn.addEventListener('click', function () { step(1); });
    // Backdrop = the dark stage around the photo (not the photo itself).
    stage.addEventListener('click', function (e) {
      var t = e.target;
      var onLayer = t === frame || (t.classList && t.classList.contains('bral__img'));
      if (t !== stage && !onLayer) return;
      if (onLayer && insideImage(e)) return;
      BA.closeOverlay();
    });
    lb.addEventListener('click', function (e) { if (e.target === lb) BA.closeOverlay(); });

    // ---- focus trap ----
    function focusables() {
      var items = lb.querySelectorAll('button, a[href], [tabindex]:not([tabindex="-1"])');
      return Array.prototype.filter.call(items, function (el) { return !el.disabled && el.offsetParent !== null; });
    }
    document.addEventListener('keydown', function (e) {
      if (!currentMode) return;
      if (e.key === 'Escape') { BA.closeOverlay(); return; }
      if (e.key === 'ArrowRight') { step(1); return; }
      if (e.key === 'ArrowLeft') { step(-1); return; }
      if (e.key !== 'Tab') return;
      var items = focusables();
      if (!items.length) return;
      var first = items[0], last = items[items.length - 1], active = document.activeElement;
      if (e.shiftKey) { if (active === first || !lb.contains(active)) { e.preventDefault(); last.focus(); } }
      else { if (active === last || !lb.contains(active)) { e.preventDefault(); first.focus(); } }
    });

    // swipe, same threshold as the forked engines (mostly-horizontal only, so
    // scrolling the panel never turns the page)
    var touch = { x: 0, y: 0, active: false };
    lb.addEventListener('touchstart', function (e) {
      if (e.touches.length !== 1) return;
      touch.x = e.touches[0].clientX;
      touch.y = e.touches[0].clientY;
      touch.active = true;
    }, { passive: true });
    lb.addEventListener('touchend', function (e) {
      if (!touch.active) return;
      touch.active = false;
      var dx = e.changedTouches[0].clientX - touch.x;
      var dy = e.changedTouches[0].clientY - touch.y;
      if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy)) step(dx < 0 ? 1 : -1);
    }, { passive: true });

    BA.registerOverlay({
      openPhoto: function (id) { openPhoto(id); },
      openWedding: function (slug) { openWedding(slug); },
      close: close
    });

    // The data an open request needs (photos.json, archive.json) may still
    // be loading when it arrives — retry once each finishes.
    var retryTimer = setInterval(function () {
      if (pendingPhoto && BA.data.photos) openPhoto(pendingPhoto);
      if (pendingWedding && BA.data.archive) openWedding(pendingWedding);
      if (!pendingPhoto && !pendingWedding) clearInterval(retryTimer);
    }, 150);

    log('ready');
  }

  boot();
})();
